import { forkJoin, merge, Observable, of, OperatorFunction, throwError } from 'rxjs';
import { map, defaultIfEmpty, catchError, shareReplay, switchMap, retry, debounceTime, tap, throttleTime } from 'rxjs/operators';

import { Translation } from './i18n';
import { getValueFromTranslation } from './language';
import { PaginatedItems } from './models';

export function mapToPaginatedItems<T>(swallowError = true) {
    return function (source: Observable<PaginatedItems>): Observable<T[]> {
        return source.pipe(
            map((x) => x.items),
            map((data) => data as T[]),
            defaultIfEmpty([]),
            catchError((err) => {
                if (swallowError) {
                    return of([]);
                }

                return throwError(() => new Error(err));
            }),
            shareReplay(1)
        );
    };
}

export interface NamedEntity {
    name: Translation | string;
    key?: string;
}

export function sortEntitiesByNameTranslation<T extends NamedEntity>() {
    return function (source: Observable<T[]>): Observable<T[]> {
        return source.pipe(
            switchMap((items: T[]) => {
                const itemsWithSortValue$ = forkJoin(
                    items.map((item) =>
                        getValueFromTranslation(item.name).pipe(
                            map((itemName) => ({
                                sortBy: itemName,
                                value: item
                            }))
                        )
                    )
                );

                return itemsWithSortValue$.pipe(
                    map((items) => items.sort((a, b) => a.sortBy.localeCompare(b.sortBy)).map((item) => item.value))
                );
            })
        );
    };
}

/**
 * Custom operator that combines throttleTime and debounceTime behavior:
 * - Emits the first value immediately
 * - Throttles intermediate values (emits at most once per interval)
 * - Always emits the last value after the interval with no new emissions
 *
 * This provides immediate UI feedback while preventing excessive processing,
 * and ensures the final state is always rendered.
 *
 * @param durationMs - The throttle/debounce duration in milliseconds
 * @param debugLabel - Optional label for debugging
 * @returns An operator function
 */
export function throttleWithTrailing<T>(durationMs: number, debugLabel?: string): OperatorFunction<T, T> {
    return (source: Observable<T>) => {
        const label = debugLabel || 'throttleWithTrailing';

        // Share the source to avoid multiple subscriptions
        const shared = source.pipe(
            tap(() => {
                if (debugLabel) {
                    console.log(`[${label}] Source emission at ${Date.now()}`);
                }
            })
        );

        return merge(
            shared.pipe(
                throttleTime(durationMs, undefined, { leading: true, trailing: false }),
                tap(() => {
                    if (debugLabel) {
                        console.log(`[${label}] Throttle emission (leading) at ${Date.now()}`);
                    }
                })
            ),
            shared.pipe(
                debounceTime(durationMs),
                tap(() => {
                    if (debugLabel) {
                        console.log(`[${label}] Debounce emission (trailing) at ${Date.now()}`);
                    }
                })
            )
        );
    };
}
